// usePortInDraft.js
//
// Hook that owns one in-progress port draft. Wraps:
//   - Initial load (get action)
//   - Local optimistic state (so the wizard never blocks on a save round-trip)
//   - Debounced autosave to update-draft (800ms minimum per Slice B2 spec)
//
// Server-side update-draft is idempotent and uses whole-array semantics on
// `numbers`, so this hook always sends the full intended state with each save.
//
// Consumers:
//   const { draft, saving, error, patchTopLevel, upsertNumber, removeNumber,
//           flush } = usePortInDraft(portRequestId);

import { useCallback, useEffect, useRef, useState } from 'react';
import { getPortRequest, updateDraft } from '../../../../../lib/portInClient';

const DEBOUNCE_MS = 800;

// Fields the server accepts in update-draft `patch`. Anything else is dropped
// to avoid `{error: "Patch contained no recognized fields."}` and to keep
// transient UI state (like portability-check responses being cached for render)
// out of the wire payload.
const PATCH_TOP_LEVEL_FIELDS = [
  'notification_emails',
  'requested_foc_date',
  // Authorized representative for Twilio's auto-LOA email flow. signer_name
  // and signer_email become losing_carrier_information.authorized_
  // representative and authorized_representative_email on PortIn create.
  // signer_title was DocuSign-specific and is no longer collected.
  'signer_name',
  'signer_email',
  // Phase D: Twilio losing_carrier_information.customer_type
  // ('Individual' | 'Business')
  'losing_carrier_customer_type',
];

// Per-number fields the server accepts. `account_pin` and `account_number` go
// over the wire as plaintext and are vaulted server-side; the response strips
// them and replaces with `account_pin_set` / `account_number_set` booleans.
const PATCH_NUMBER_FIELDS = [
  'phone_number',
  'losing_carrier',
  'losing_carrier_detected_value',
  'losing_carrier_detected_at',
  'portability',
  'billing_zip',
  'account_holder_name',
  'service_address',
  'account_pin',
  'account_number',
];

function pickPatchableNumber(n) {
  const out = {};
  for (const k of PATCH_NUMBER_FIELDS) {
    if (n[k] !== undefined) out[k] = n[k];
  }
  return out;
}

function buildPatch(draft) {
  const patch = {};
  if (Array.isArray(draft.numbers)) {
    patch.numbers = draft.numbers.map(pickPatchableNumber);
  }
  for (const k of PATCH_TOP_LEVEL_FIELDS) {
    if (draft[k] !== undefined) patch[k] = draft[k];
  }
  return patch;
}

// Server-authoritative top-level fields. When a save response comes back we
// trust the server's value for these (status transitions, vault IDs,
// Twilio/DocuSign SIDs, server-stamped timestamps). Everything else is
// user-authoritative: the server may echo the latest values it persisted
// but we DON'T overwrite the local draft because the user may have typed
// new characters into a field while the save was in flight (the original
// bug this exists to fix: type PIN, tab to ZIP, start typing ZIP, the
// in-flight PIN save returns with the older ZIP value and clobbers the
// new keystrokes).
const SERVER_AUTHORITATIVE_TOP_LEVEL = [
  'status',
  'status_message',
  'rejection_reason',
  'rejection_code',
  'updated_at',
  'submitted_at',
  'completed_at',
  'canceled_at',
  'twilio_port_in_sid',
  'twilio_target_subaccount_sid',
  'docusign_envelope_id',
  'loa_twilio_document_sid',
  'bill_twilio_document_sid',
  'bill_storage_path',
  'messaging_service_sid',
  'actual_foc_date',
  'created_at',
  'created_by',
  'id',
  'tenant_id',
];

// Per-number server-authoritative fields. Same principle: trust the server
// for the "is the secret vaulted?" booleans and the vault IDs themselves;
// trust the user for everything they're typing.
const SERVER_AUTHORITATIVE_NUMBER_FIELDS = [
  'account_pin_set',
  'account_number_set',
  'account_pin_vault_id',
  'account_number_vault_id',
  'individual_status',
  'losing_carrier_detected_value',
  'losing_carrier_detected_at',
];

/**
 * Merge a server response (`incoming`) into the current local draft
 * (`current`) without clobbering fields the user may have edited while the
 * save was in flight. Server-authoritative fields always win. User-
 * authoritative fields keep their current local value.
 *
 * Also strips the plaintext PIN / account number off any number entry the
 * server has confirmed as vaulted (account_pin_set / account_number_set =
 * true), so the next save doesn't re-vault the same value.
 */
function mergeServerResponse(current, incoming) {
  if (!current) return incoming;
  if (!incoming) return current;

  const next = { ...current };

  for (const k of SERVER_AUTHORITATIVE_TOP_LEVEL) {
    if (incoming[k] !== undefined) next[k] = incoming[k];
  }

  if (Array.isArray(incoming.numbers)) {
    const localNumbers = Array.isArray(current.numbers) ? current.numbers : [];
    const localByPhone = new Map(localNumbers.map((n) => [n.phone_number, n]));
    const merged = [];
    for (const remote of incoming.numbers) {
      const local = localByPhone.get(remote.phone_number);
      if (!local) {
        // Server added a number we don't have locally. Trust the server.
        merged.push(remote);
        continue;
      }
      localByPhone.delete(remote.phone_number);
      const m = { ...local };
      for (const k of SERVER_AUTHORITATIVE_NUMBER_FIELDS) {
        if (remote[k] !== undefined) m[k] = remote[k];
      }
      // Once server confirms vault write, drop the plaintext from local
      // state so subsequent saves don't re-vault it.
      if (remote.account_pin_set === true) delete m.account_pin;
      if (remote.account_number_set === true) delete m.account_number;
      merged.push(m);
    }
    // Anything still in localByPhone is a local-only number the server
    // didn't echo back. Drop it: server is authoritative on which numbers
    // exist on the draft (e.g. it may have dedupe'd them).
    next.numbers = merged;
  }

  return next;
}

export function usePortInDraft(portRequestId) {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  // Wizard-runtime carrier metadata keyed by E.164 phone. Populated by
  // portability-check / lookup-carrier responses. NOT persisted to the draft
  // (it's derivable from `losing_carrier_detected_value` server-side), kept
  // in memory only so Step 2 can render help text without re-hitting the
  // carrier lookup API on every re-render.
  const [carrierByPhone, setCarrierByPhone] = useState({});

  const draftRef = useRef(draft);
  const saveTimerRef = useRef(null);
  const dirtyRef = useRef(false);
  const inFlightRef = useRef(false);
  const unmountedRef = useRef(false);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    return () => {
      unmountedRef.current = true;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  // Initial load
  useEffect(() => {
    if (!portRequestId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const { data, error: getErr } = await getPortRequest(portRequestId);
      if (cancelled) return;
      if (getErr) {
        setError(getErr.message || 'Failed to load port request.');
        setLoading(false);
        return;
      }
      const pr = data?.portRequest ?? null;
      if (!pr) {
        setError('Port request not found.');
        setLoading(false);
        return;
      }
      // Normalize: ensure numbers + emails are arrays so step components can
      // map/spread without null-guards.
      pr.numbers = Array.isArray(pr.numbers) ? pr.numbers : [];
      pr.notification_emails = Array.isArray(pr.notification_emails) ? pr.notification_emails : [];
      setDraft(pr);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [portRequestId]);

  const persist = useCallback(async () => {
    const current = draftRef.current;
    if (!current || !portRequestId) return;
    if (inFlightRef.current) {
      // A save is already running. Mark dirty so we re-fire after it returns.
      dirtyRef.current = true;
      return;
    }
    inFlightRef.current = true;
    dirtyRef.current = false;
    setSaving(true);
    setError(null);

    const patch = buildPatch(current);
    const { data, error: updErr } = await updateDraft(portRequestId, patch);
    inFlightRef.current = false;

    if (unmountedRef.current) return;

    if (updErr) {
      setError(updErr.message || 'Failed to save.');
      setSaving(false);
      return;
    }
    const pr = data?.portRequest;
    if (pr) {
      pr.numbers = Array.isArray(pr.numbers) ? pr.numbers : [];
      pr.notification_emails = Array.isArray(pr.notification_emails) ? pr.notification_emails : [];
      // Merge the server response into local state using the field-level
      // policy in mergeServerResponse: server-authoritative fields (status,
      // vault IDs, SIDs, timestamps) get applied; user-authoritative fields
      // (anything the customer is currently typing into) keep their local
      // value so in-flight edits aren't clobbered. Plaintext PIN / account
      // number are dropped once the server confirms vault writes.
      setDraft((curr) => {
        const merged = mergeServerResponse(curr, pr);
        draftRef.current = merged;
        return merged;
      });
    }
    setSaving(false);

    if (dirtyRef.current) {
      // Another edit landed mid-save. Schedule a follow-up so we converge.
      scheduleSave();
    }
  }, [portRequestId]);

  const scheduleSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      persist();
    }, DEBOUNCE_MS);
  }, [persist]);

  /**
   * Force any pending save to run NOW and wait for it. Useful when the user
   * advances to the next wizard step and we want to flush before navigating.
   */
  const flush = useCallback(async () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    await persist();
    // If a save was already in flight when flush was called, `persist` will
    // have queued itself again via dirtyRef. Wait it out.
    while (inFlightRef.current) {
      await new Promise((r) => setTimeout(r, 50));
    }
  }, [persist]);

  const patchTopLevel = useCallback(
    (partial) => {
      setDraft((prev) => {
        if (!prev) return prev;
        return { ...prev, ...partial };
      });
      scheduleSave();
    },
    [scheduleSave]
  );

  /**
   * Upsert a single number entry by E.164 phone_number. Pass the full intended
   * shape for that entry; existing fields are merged.
   */
  const upsertNumber = useCallback(
    (phoneE164, partial) => {
      setDraft((prev) => {
        if (!prev) return prev;
        const numbers = Array.isArray(prev.numbers) ? prev.numbers : [];
        const idx = numbers.findIndex((n) => n.phone_number === phoneE164);
        if (idx === -1) {
          return {
            ...prev,
            numbers: [...numbers, { phone_number: phoneE164, ...partial }],
          };
        }
        const next = numbers.slice();
        next[idx] = { ...numbers[idx], ...partial };
        return { ...prev, numbers: next };
      });
      scheduleSave();
    },
    [scheduleSave]
  );

  const removeNumber = useCallback(
    (phoneE164) => {
      setDraft((prev) => {
        if (!prev) return prev;
        const numbers = Array.isArray(prev.numbers) ? prev.numbers : [];
        return { ...prev, numbers: numbers.filter((n) => n.phone_number !== phoneE164) };
      });
      scheduleSave();
    },
    [scheduleSave]
  );

  /**
   * Replace the entire numbers array at once. Useful when Step 1 imports a
   * pasted list and we want to atomically swap.
   */
  const replaceNumbers = useCallback(
    (nextNumbers) => {
      setDraft((prev) => {
        if (!prev) return prev;
        return { ...prev, numbers: Array.isArray(nextNumbers) ? nextNumbers : [] };
      });
      scheduleSave();
    },
    [scheduleSave]
  );

  const setCarrier = useCallback((phoneE164, carrierMeta) => {
    if (!phoneE164) return;
    setCarrierByPhone((prev) => ({ ...prev, [phoneE164]: carrierMeta }));
  }, []);

  /**
   * Refetch the draft from the server. Used after server-mutating side-effects
   * the hook does not otherwise know about (e.g. Phase D's upload-bill, which
   * sets bill_twilio_document_sid + may auto-promote status to submitted).
   * Skips when there's a save in flight to avoid clobbering local edits.
   */
  const reload = useCallback(async () => {
    if (!portRequestId) return;
    if (inFlightRef.current) return;
    const { data, error: getErr } = await getPortRequest(portRequestId);
    if (unmountedRef.current) return;
    if (getErr) {
      setError(getErr.message || 'Failed to reload port request.');
      return;
    }
    const pr = data?.portRequest;
    if (!pr) return;
    pr.numbers = Array.isArray(pr.numbers) ? pr.numbers : [];
    pr.notification_emails = Array.isArray(pr.notification_emails) ? pr.notification_emails : [];
    // Same merge policy as persist's response handler: preserve any local
    // edits in user-authoritative fields, trust server for status / SIDs /
    // vault state.
    setDraft((curr) => {
      const merged = mergeServerResponse(curr, pr);
      draftRef.current = merged;
      return merged;
    });
  }, [portRequestId]);

  return {
    draft,
    loading,
    saving,
    error,
    validationErrors,
    setValidationErrors,
    patchTopLevel,
    upsertNumber,
    removeNumber,
    replaceNumbers,
    flush,
    reload,
    carrierByPhone,
    setCarrier,
  };
}
